/**
 * Absence + delegation on Today — V98-3-4 (ADR-0046 T2–T5; research §2
 * rules 9, 14, 17, 18, 23). Coverage applies to every hat.
 *
 *   PendingAbsence   someone else proposed your absence: what you must sign
 *                    BEFORE you leave (non-delegable hard dates inside the
 *                    window, S9) first, then the consequence preview (where
 *                    each owed item lands, uncovered days) + Confirm /
 *                    Decline with a reason. Nothing moves until you confirm.
 *                    On a phone it collapses to one row (U3).
 *   AwayNow          you're out: "I'm back" ends it.
 *   WhileYouWereOut  on return: done for you, handled elsewhere, MISSED,
 *                    still pending — every row opens its item; Dismiss is
 *                    remembered on this device (U14).
 *   OutOfOffice      set your own absence, optionally delegate. The preview
 *                    is ALWAYS shown before saving (exact items that move,
 *                    the ones that stay and why, uncovered days, candidate
 *                    conflicts) — a dry run of the same combined call. The
 *                    absence and the delegation are saved in ONE request
 *                    (POST /coverage/absence-with-delegation): both or
 *                    neither, and a retry never creates a second absence
 *                    (U14).
 *   DelegateBanner   you're covering for someone: until when, how many.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Field, Input, Select, Textarea } from '@aigovops/design-system';
import type { SignBeforeLeaving, UpcomingAbsence } from '@aigovops/core';
import {
  absencePreview, confirmAbsence, declineAbsence, editAbsence, endAbsence, listCovering, listMyAbsences, saveAbsenceWithDelegation,
  whileYouWereOut, type AbsencePreview, type AbsenceWithDelegationBody, type AbsenceWithDelegationView, type DelegationPreview, type OwedItem,
} from '../../lib/coverage-api';
import { invalidationMap } from '../../lib/query-keys';
import { firstName, fmtDay, itemHref, nameOf, type Names } from '../../lib/today-model';
import { ActionDialog, Chip, domId, errorText } from './shared';

type Done = (msg: string, focusId: string) => void;

const SCOPES: Array<{ id: string; label: string }> = [
  { id: 'policy', label: 'Policies' },
  { id: 'risk_acceptance', label: 'Risk acceptances' },
  { id: 'access_review', label: 'Access reviews' },
  { id: 'scn', label: 'Change notices' },
  { id: 'management_response', label: 'Management responses' },
  { id: 'work_item', label: 'Owned work items' },
];

async function refresh(qc: ReturnType<typeof useQueryClient>, slug: string) {
  await Promise.all([
    ...invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })),
    qc.invalidateQueries({ queryKey: ['coverage', slug] }),
  ]);
}

export function useAbsences(slug: string, userId: string) {
  return useQuery({ queryKey: ['coverage', slug, 'absences', userId], queryFn: () => listMyAbsences(slug, userId), staleTime: 30_000, retry: false });
}

const landingText = (p: AbsencePreview['items'][number], names: Names) => {
  const l = p.landing;
  if (p.lands_on_preparer) return { text: `Can’t route to ${firstName(nameOf(names, l.user_id))} · they prepared it`, tone: 'amber' as const };
  if (l.status === 'routed' || l.status === 'delegated') return { text: `Goes to ${nameOf(names, l.user_id)}`, tone: 'neutral' as const };
  if (l.status === 'stays') return { text: 'Stays with you', tone: 'neutral' as const };
  return { text: l.reason_text?.[0] ?? l.reasons[0] ?? 'Waits for you', tone: 'amber' as const }; // R3-S8
};

/** The landing preview list (shared by the pending card and the ask page). */
export function AbsencePreviewList({ p, names, prefix }: { p: AbsencePreview; names: Names; prefix: string }) {
  const blocked = p.items.filter((i) => i.lands_on_preparer);
  return (
    <>
      <ul className="gp-owed-list" aria-label="Where the items go">
        {p.items.map((i) => {
          const l = landingText(i, names);
          return (
            <li key={`${i.item.kind}:${i.item.id}`} className="gp-ow">
              <span className="gp-ow-main"><Link className="gp-ow-t" to={itemHref(prefix, { kind: i.item.kind, id: i.item.id })}>{i.item.title}</Link></span>
              <span className="gp-ow-meta"><Chip text={l.text} tone={l.tone} /></span>
            </li>
          );
        })}
      </ul>
      {p.gaps.length ? <p><Chip text={`Not covered: ${p.gaps.map((g) => `${fmtDay(g.from)} – ${fmtDay(g.to)}`).join(', ')}`} tone="amber" /></p> : null}
      {blocked.length ? <Alert variant="warning">{`${blocked.length} item${blocked.length === 1 ? '' : 's'} can’t go to the person who prepared ${blocked.length === 1 ? 'it' : 'them'}; ${blocked.length === 1 ? 'it waits' : 'they wait'} for you or another approver.`}</Alert> : null}
    </>
  );
}

export function PendingAbsence({ slug, absence, names, prefix, signBefore, compact, onDone }: {
  slug: string; absence: { absence_id: string; starts_on: string; ends_on: string; set_by: string; preview?: AbsencePreview }; names: Names; prefix: string;
  /** S9: non-delegable signatures whose hard date falls inside this absence — sign them first. */
  signBefore: readonly SignBeforeLeaving[];
  /** Phone: one row that expands (U3). */
  compact: boolean;
  onDone: Done;
}) {
  const qc = useQueryClient();
  const preview = useQuery({ queryKey: ['coverage', slug, 'preview', absence.absence_id], queryFn: () => absencePreview(slug, absence.absence_id), enabled: !absence.preview, retry: false });
  const p = absence.preview ?? preview.data?.preview;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonErr, setReasonErr] = useState<string | null>(null);
  const [declineErr, setDeclineErr] = useState<string | null>(null);
  const mine = signBefore.filter((s) => s.absence_id === absence.absence_id);
  const confirm = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try { await confirmAbsence(slug, absence.absence_id); await refresh(qc, slug); onDone('Out of office confirmed', 'today-h1'); }
    catch (e) { setError(errorText(e, 'The absence could not be confirmed.')); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    if (!reason.trim()) { setReasonErr('Give a reason (required).'); return false; }
    setBusy(true); setDeclineErr(null);
    try { await declineAbsence(slug, absence.absence_id, reason.trim()); setDeclining(false); await refresh(qc, slug); onDone('Proposed absence declined', 'today-h1'); return true; }
    catch (e) { setDeclineErr(errorText(e, 'The absence could not be declined.')); return false; }
    finally { setBusy(false); }
  };
  const heading = `Confirm your out of office · ${fmtDay(absence.starts_on)} – ${fmtDay(absence.ends_on)}`;
  const body = (
    <>
      <p className="gp-dim">{`Proposed by ${nameOf(names, absence.set_by)}. Nothing moves until you confirm.`}</p>
      {mine.length ? (
        <div className="gp-state gp-state--warn" data-part="sign-before-leaving">
          <strong>{`Sign before you leave · ${mine.length}`}</strong>
          <ul className="gp-plain-list" aria-label="Sign before you leave">
            {mine.map((s) => (
              <li key={s.key} className="gp-ow-line">
                <a className="gp-ow-t" href={`#${domId('ow', s.key)}`}>{s.title}</a>
                <span className="gp-dim">{` · hard date ${fmtDay(s.hard_date)} · can’t be delegated`}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {p ? <AbsencePreviewList p={p} names={names} prefix={prefix} /> : preview.isLoading ? <p className="gp-dim">Loading where your items go…</p> : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div className="gp-actions">
        <Button variant={mine.length ? 'secondary' : 'primary'} aria-disabled={busy || undefined} onClick={() => void confirm()}>Confirm out of office</Button>
        <Button id="pending-abs-decline" onClick={() => setDeclining(true)}>Decline…</Button>
      </div>
      <ActionDialog open={declining} title="Decline the proposed absence" submitLabel="Decline" busy={busy} error={declineErr}
        onClose={() => { setDeclining(false); setDeclineErr(null); setReasonErr(null); }} onSubmit={decline}>
        <Field id="decline-reason" label="Reason" required hint="Required · up to 2000 characters · shared with the proposer" error={reasonErr ?? undefined}>
          <Textarea rows={3} maxLength={2000} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setReasonErr(null); setDeclineErr(null); }} />
        </Field>
      </ActionDialog>
    </>
  );
  if (compact) {
    return (
      <details className="gp-panel gp-collapsible" id="pending-absence" data-part="pending-absence">
        <summary>
          <span className="gp-collapsible-title">{`Out of office ${fmtDay(absence.starts_on)} – ${fmtDay(absence.ends_on)} · confirm`}</span>
          {mine.length ? <Chip text={`${mine.length} to sign first`} tone="amber" /> : null}
          {/* R3-U11: the folded row says it opens. */}
          <span className="gp-collapsible-cue" aria-hidden="true">Review</span>
        </summary>
        <h2 id="pending-abs-h" className="gp-sr-only">{heading}</h2>
        {body}
      </details>
    );
  }
  return (
    <section className="gp-panel" aria-labelledby="pending-abs-h" id="pending-absence" data-part="pending-absence">
      <h2 id="pending-abs-h">{heading}</h2>
      {body}
    </section>
  );
}

/**
 * R3-S6 / Blocker: your own CONFIRMED absence that hasn't started — one row per absence with what you must
 * sign before you leave (non-delegable hard dates inside it, each a link to its item), Edit (new dates, the
 * server re-routes) and Cancel (with a confirm). Another, non-overlapping absence can still be set below.
 */
export function UpcomingAbsences({ slug, rows, prefix, todayDay, onDone }: {
  slug: string; rows: readonly UpcomingAbsence[]; prefix: string; todayDay: string; onDone: Done;
}) {
  if (!rows.length) return null;
  return (
    <section className="gp-panel" aria-labelledby="upcoming-abs-h" data-part="upcoming-absences">
      <h2 id="upcoming-abs-h" tabIndex={-1}>{rows.length === 1 ? 'Your upcoming out of office' : `Your upcoming out of office · ${rows.length}`}</h2>
      <ul className="gp-plain-list gp-form" aria-label="Upcoming out of office">
        {rows.map((a) => <UpcomingAbsenceRow key={a.absence_id} slug={slug} a={a} prefix={prefix} todayDay={todayDay} onDone={onDone} />)}
      </ul>
    </section>
  );
}

function UpcomingAbsenceRow({ slug, a, prefix, todayDay, onDone }: { slug: string; a: UpcomingAbsence; prefix: string; todayDay: string; onDone: Done }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [from, setFrom] = useState(a.starts_on);
  const [to, setTo] = useState(a.ends_on);
  const [errors, setErrors] = useState<{ from?: string; to?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelErr, setCancelErr] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) firstField.current?.focus(); }, [editing]);
  const rowId = domId('upcoming', a.absence_id);
  const editId = domId('upcoming-edit', a.absence_id);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!from) errs.from = 'Choose your first day out.';
    else if (from < todayDay) errs.from = 'Starts today or later.';
    if (!to) errs.to = 'Choose your last day out.';
    else if (from && to < from) errs.to = 'Ends on or after the first day.';
    setErrors(errs);
    if (errs.from || errs.to) return;
    setBusy(true); setError(null);
    try {
      const r = await editAbsence(slug, a.absence_id, { starts_on: from, ends_on: to });
      setEditing(false);
      await refresh(qc, slug);
      onDone(`Out of office moved to ${fmtDay(from)} – ${fmtDay(to)}${r.rerouted?.length ? ` · ${r.rerouted.length} item${r.rerouted.length === 1 ? '' : 's'} re-routed` : ''}`, rowId);
    } catch (err) { setError(errorText(err, 'The new dates could not be saved.')); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true); setCancelErr(null);
    try { await endAbsence(slug, a.absence_id); setCancelling(false); await refresh(qc, slug); onDone(`Out of office ${fmtDay(a.starts_on)} – ${fmtDay(a.ends_on)} cancelled`, 'ooo-h'); return true; }
    catch (err) { setCancelErr(errorText(err, 'The out of office could not be cancelled.')); return false; }
    finally { setBusy(false); }
  };
  return (
    <li className="gp-form" id={rowId} tabIndex={-1} data-absence={a.absence_id}>
      <div className="gp-ow-line">
        <strong>{`${fmtDay(a.starts_on)} – ${fmtDay(a.ends_on)}`}</strong>
        <Chip text="Confirmed" tone="ok" />
      </div>
      {a.sign_before_leaving.length ? (
        <div className="gp-state gp-state--warn" data-part="sign-before-leaving">
          <strong>{`Sign before you leave · ${a.sign_before_leaving.length}`}</strong>
          <ul className="gp-plain-list" aria-label={`Sign before you leave · ${fmtDay(a.starts_on)}`}>
            {a.sign_before_leaving.map((x) => (
              <li key={x.key} className="gp-ow-line">
                <Link className="gp-ow-t" to={itemHref(prefix, x.ref)}>{x.title}</Link>
                <span className="gp-dim">{` · hard date ${fmtDay(x.hard_date)} · can’t be delegated`}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : <p className="gp-dim">Nothing to sign before you leave.</p>}
      {editing ? (
        <form className="gp-form" onSubmit={save} noValidate aria-label={`New dates · ${fmtDay(a.starts_on)} – ${fmtDay(a.ends_on)}`}>
          {error ? <Alert variant="error">{error}</Alert> : null}
          <div className="gp-form-row">
            <Field id={`${editId}-from`} label="First day out" required error={errors.from}>
              <Input ref={firstField} type="date" min={todayDay} value={from} onChange={(e) => { setFrom(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, from: undefined })); }} />
            </Field>
            <Field id={`${editId}-to`} label="Last day out" required error={errors.to}>
              <Input type="date" min={from || todayDay} value={to} onChange={(e) => { setTo(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, to: undefined })); }} />
            </Field>
          </div>
          <div className="gp-actions">
            <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save new dates'}</Button>
            <Button variant="ghost" onClick={() => { setEditing(false); setErrors({}); setError(null); setFrom(a.starts_on); setTo(a.ends_on); window.setTimeout(() => document.getElementById(editId)?.focus(), 0); }}>Cancel</Button>
          </div>
        </form>
      ) : (
        <div className="gp-actions">
          <Button compact id={editId} aria-expanded={false} aria-label={`Edit dates · out of office ${fmtDay(a.starts_on)} – ${fmtDay(a.ends_on)}`} onClick={() => setEditing(true)}>Edit dates</Button>
          <Button compact id={domId('upcoming-cancel', a.absence_id)} aria-label={`Cancel out of office ${fmtDay(a.starts_on)} – ${fmtDay(a.ends_on)}`} onClick={() => setCancelling(true)}>Cancel…</Button>
        </div>
      )}
      <ActionDialog open={cancelling} title={`Cancel out of office · ${fmtDay(a.starts_on)} – ${fmtDay(a.ends_on)}`} submitLabel="Cancel out of office" cancelLabel="Keep it" danger busy={busy} error={cancelErr}
        onClose={() => { setCancelling(false); setCancelErr(null); }} onSubmit={cancel}>
        <p>Your items stay with you; nothing is delegated for these dates.</p>
      </ActionDialog>
    </li>
  );
}

export function AwayNow({ slug, absence, onDone }: { slug: string; absence: { absence_id: string; ends_on: string }; onDone: Done }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const back = async () => {
    try { await endAbsence(slug, absence.absence_id); await refresh(qc, slug); onDone('Welcome back · your items are with you again', 'today-h1'); }
    catch (e) { setError(errorText(e, 'Could not end the absence.')); }
  };
  return (
    <section className="gp-panel" aria-labelledby="away-h">
      <h2 id="away-h">{`You’re out until ${fmtDay(absence.ends_on)}`}</h2>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div className="gp-actions"><Button onClick={() => void back()}>I’m back</Button></div>
    </section>
  );
}

const DISMISS_KEY = (absenceId: string) => `gp-wywo-dismissed:${absenceId}`;
function readDismissed(absenceId: string): boolean {
  try { return localStorage.getItem(DISMISS_KEY(absenceId)) === '1'; } catch { return false; }
}

export function WhileYouWereOut({ slug, absenceId, names, prefix, onDone }: { slug: string; absenceId: string; names: Names; prefix: string; onDone: Done }) {
  const q = useQuery({ queryKey: ['coverage', slug, 'wywo', absenceId], queryFn: () => whileYouWereOut(slug, absenceId), retry: false });
  const [dismissed, setDismissed] = useState(() => readDismissed(absenceId));
  useEffect(() => { setDismissed(readDismissed(absenceId)); }, [absenceId]);
  const s = q.data?.summary;
  if (!s || dismissed) return null;
  const item = (kind: string, id: string, title: string, chip: string, tone: 'ok' | 'amber' | 'neutral' | 'lock', key: string) => (
    <li key={key} className="gp-ow">
      <span className="gp-ow-main"><Link className="gp-ow-t" to={itemHref(prefix, { kind, id })}>{title}</Link></span>
      <span className="gp-ow-meta"><Chip text={chip} tone={tone} /></span>
    </li>
  );
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY(absenceId), '1'); } catch { /* storage unavailable: hidden for this view only */ }
    setDismissed(true);
    onDone('While you were out dismissed', 'owed-h');
  };
  return (
    <section className="gp-panel" aria-labelledby="wywo-h">
      <h2 id="wywo-h">{`While you were out · ${fmtDay(s.absence.starts_on)} – ${fmtDay(s.absence.ends_on)}`}</h2>
      <ul className="gp-owed-list" aria-label="While you were out">
        {s.missed.map((m) => item(m.kind, m.id, m.title, 'Missed · still open', 'amber', `m-${m.id}`))}
        {s.done_for_you.map((d) => item(d.kind, d.id, d.title, `Done for you · ${nameOf(names, d.by)}`, 'ok', `d-${d.id}`))}
        {s.routed_elsewhere.map((d) => item(d.kind, d.id, d.title, `Handled by ${nameOf(names, d.by)}`, 'lock', `r-${d.id}`))}
        {s.still_pending.map((m: OwedItem) => item(m.kind, m.id, m.title, 'Still yours', 'neutral', `p-${m.id}`))}
      </ul>
      <div className="gp-actions">
        {s.missed.length ? <a className="gp-linkbtn gp-linkbtn--compact" href="#owed">{`Go to Owed · ${s.missed.length} missed`}</a> : null}
        <Button compact onClick={dismiss}>Dismiss</Button>
      </div>
    </section>
  );
}

export function DelegateBanner({ slug, names, count }: { slug: string; names: Names; count: number }) {
  const q = useQuery({ queryKey: ['coverage', slug, 'covering'], queryFn: () => listCovering(slug), staleTime: 60_000, retry: false });
  const ds = q.data?.covering.map((c) => c.delegation) ?? [];
  const who = ds.map((d) => `${nameOf(names, d.delegator_user_id)} until ${fmtDay(d.ends_at)}`).join(' · ');
  return (
    <Alert variant="info" title={who ? `Covering for ${who}` : 'Covering for a colleague'}>
      {`${count} item${count === 1 ? '' : 's'} tagged “For …” in Owed are theirs; you sign or approve on their behalf, recorded under the delegation.`}
    </Alert>
  );
}

// ---- set your own out of office (+ delegation preview) ------------------------------------

/** One sentence for screen readers when a preview arrives (U12: one summary, not the whole list). */
export function previewSummary(p: DelegationPreview, names: Names, delegate: boolean): string {
  const who = p.delegate_user_id ? firstName(nameOf(names, p.delegate_user_id)) : 'your delegate';
  const gaps = [...p.gaps, ...p.pending_gaps];
  return [
    delegate ? `${p.items_moving.length} move to ${who}` : `${p.items_moving.length + p.items_staying.length} stay with you or route to a backup`,
    delegate && p.items_staying.length ? `${p.items_staying.length} stay with you` : null,
    gaps.length ? `days not covered: ${gaps.map((g) => `${fmtDay(g.from)} – ${fmtDay(g.to)}`).join(', ')}` : null,
  ].filter(Boolean).join(' · ');
}

/** The same, for an absence with no delegation: where the items go while you're away. */
export function absenceSummary(p: AbsencePreview): string {
  const moving = p.items.filter((i) => i.landing.status === 'routed' || i.landing.status === 'delegated').length;
  return [
    `${moving} go to someone else`,
    p.items.length - moving ? `${p.items.length - moving} wait for you` : null,
    p.gaps.length ? `days not covered: ${p.gaps.map((g) => `${fmtDay(g.from)} – ${fmtDay(g.to)}`).join(', ')}` : null,
  ].filter(Boolean).join(' · ');
}

export function OutOfOffice({ slug, viewerId, names, todayDay, onDone }: { slug: string; viewerId: string; names: Names; todayDay: string; onDone: Done }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [delegate, setDelegate] = useState(false);
  const [who, setWho] = useState('');
  const [scope, setScope] = useState<string[]>(['policy', 'risk_acceptance', 'access_review']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** The dry run of the exact request Save sends (absence + delegation together). */
  const [preview, setPreview] = useState<AbsenceWithDelegationView | null>(null);
  const [summary, setSummary] = useState('');
  /** Who conflicts on what, from the last preview — kept when Back returns to step 1 so the picker can say it (R3-U11). */
  const [candidateInfo, setCandidateInfo] = useState<DelegationPreview['candidates']>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!from) e.from = 'Choose your first day out.';
    else if (from < todayDay) e.from = 'Starts today or later.';
    if (!to) e.to = 'Choose your last day out.';
    else if (from && to < from) e.to = 'Ends on or after the first day.';
    if (delegate && !who) e.who = 'Choose who covers for you.';
    if (delegate && !scope.length) e.scope = 'Choose at least one kind of item.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  // The delegation window is the absence days (the server's default; a day end is inclusive).
  const request = (): AbsenceWithDelegationBody => ({ starts_on: from, ends_on: to, delegation: delegate ? { delegate_user_id: who, scope } : null });
  const runPreview = async () => {
    if (!validate()) return;
    setBusy(true); setError(null);
    try {
      const p = await saveAbsenceWithDelegation(slug, { ...request(), dry_run: true });
      // R3-S7: the server answers what a save would refuse (blocked + reasons) instead of an error.
      if (p.blocked || !p.preview) { setPreview(null); setError((p.reasons ?? []).map((r) => r.message).join(' ') || 'This absence can’t be saved as it is.'); return; }
      setPreview(p);
      if (p.delegation_preview) setCandidateInfo(p.delegation_preview.candidates);
      setSummary(`Preview · ${p.delegation_preview ? previewSummary(p.delegation_preview, names, true) : absenceSummary(p.preview)}`);
    } catch (e) { setError(errorText(e, 'The preview could not load.')); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!validate()) return false;
    // The preview is ALWAYS shown before anything is saved (U14).
    if (!preview) { await runPreview(); return false; }
    setBusy(true); setError(null);
    try {
      // One request: both or neither. A retry after a lost response returns the saved records (`replayed`).
      await saveAbsenceWithDelegation(slug, request());
      setOpen(false); setPreview(null); setSummary('');
      await refresh(qc, slug);
      onDone(`Out of office saved · ${fmtDay(from)} – ${fmtDay(to)}${delegate ? ` · ${firstName(nameOf(names, who))} covers` : ''}`, 'ooo-h');
      return true;
    } catch (e) {
      setError(`Nothing was saved: ${errorText(e, 'the out of office could not be saved')}`);
      return false;
    } finally { setBusy(false); }
  };
  const conflictsOf = (u: string) => candidateInfo.find((c) => c.user_id === u);
  const candidates = [...names.keys()].filter((u) => u !== viewerId);
  const stale = () => { setPreview(null); setSummary(''); setError(null); };
  const close = () => { setOpen(false); setPreview(null); setErrors({}); setError(null); setSummary(''); };
  return (
    <section className="gp-panel" aria-labelledby="ooo-h">
      <h2 id="ooo-h" tabIndex={-1}>Out of office</h2>
      <div className="gp-actions"><Button id="ooo-open" onClick={() => setOpen(true)}>Set out of office…</Button></div>
      <ActionDialog open={open} title="Out of office" submitLabel={!preview ? 'Preview' : 'Save'} busy={busy} error={error}
        step={{ n: preview ? 2 : 1, of: 2, label: preview ? 'Check what moves, then save' : 'Dates and cover' }}
        back={preview ? { label: 'Back', onClick: stale } : null}
        onClose={close} onSubmit={save}>
        {preview ? (
          // R3-U11: step 2 leads with the preview; the choices fold to one line, and Back returns to them.
          <p className="gp-choice-line" data-part="ooo-choice">
            <span>{`${fmtDay(from)} – ${fmtDay(to)}`}</span>
            <span>{delegate && who ? `· ${firstName(nameOf(names, who))} covers ${scope.map((x) => SCOPES.find((y) => y.id === x)?.label.toLowerCase() ?? x).join(', ')}` : '· no delegation'}</span>
          </p>
        ) : (
          <>
          <div className="gp-form-row">
            <Field id="ooo-from" label="First day out" required error={errors.from}>
              <Input type="date" min={todayDay} value={from} onChange={(e) => { setFrom(e.currentTarget.value); stale(); setErrors((x) => ({ ...x, from: '' })); }} />
            </Field>
            <Field id="ooo-to" label="Last day out" required error={errors.to}>
              <Input type="date" min={from || todayDay} value={to} onChange={(e) => { setTo(e.currentTarget.value); stale(); setErrors((x) => ({ ...x, to: '' })); }} />
            </Field>
          </div>
          <Checkbox id="ooo-delegate" label="Delegate while I’m out" checked={delegate} onChange={(e) => { setDelegate(e.currentTarget.checked); stale(); }} />
          {delegate ? (
            <>
              <Field id="ooo-who" label="Delegate to" required error={errors.who}>
                <Select value={who} onChange={(e) => { setWho(e.currentTarget.value); stale(); setErrors((x) => ({ ...x, who: '' })); }}>
                  <option value="">Choose…</option>
                  {candidates.map((u) => {
                    const c = conflictsOf(u);
                    const extra = c ? (c.conflicts.length ? ` · conflict on ${c.conflicts.length}` : '') + (c.away_during ? ' · away too' : '') : '';
                    return <option key={u} value={u}>{`${nameOf(names, u)}${extra}`}</option>;
                  })}
                </Select>
              </Field>
              <fieldset className="gp-fieldset" aria-describedby={errors.scope ? 'ooo-scope-err' : 'ooo-scope-hint'} aria-invalid={errors.scope ? true : undefined}>
                <legend>What they can act on</legend>
                {SCOPES.map((s) => (
                  <Checkbox key={s.id} id={`ooo-scope-${s.id}`} label={s.label} checked={scope.includes(s.id)}
                    onChange={(e) => { const on = e.currentTarget.checked; setScope((x) => (on ? [...x, s.id] : x.filter((y) => y !== s.id))); stale(); setErrors((x) => ({ ...x, scope: '' })); }} />
                ))}
                {errors.scope ? <p id="ooo-scope-err" role="alert" className="gp-field-err">{errors.scope}</p> : null}
                <p id="ooo-scope-hint" className="gp-dim">Certification packages and system descriptions can’t be delegated.</p>
              </fieldset>
            </>
          ) : null}
          </>
        )}
        {/* One summary sentence is announced; the list itself is not a live region (U12). */}
        <p className="gp-sr-only" role="status" aria-live="polite">{summary}</p>
        {preview?.delegation_preview ? <DelegationPreviewView p={preview.delegation_preview} names={names} delegate={delegate} />
          : preview?.preview ? <div data-part="absence-preview"><AbsencePreviewList p={preview.preview} names={names} prefix={`/w/${slug}/app`} /></div> : null}
      </ActionDialog>
    </section>
  );
}

function DelegationPreviewView({ p, names, delegate }: { p: DelegationPreview; names: Names; delegate: boolean }) {
  const who = p.delegate_user_id ? firstName(nameOf(names, p.delegate_user_id)) : 'your delegate';
  const gaps = [...p.gaps, ...p.pending_gaps];
  return (
    <div className="gp-form" data-part="delegation-preview">
      {delegate ? (
        <>
          <h3>{`Moves to ${who} · ${p.items_moving.length}`}</h3>
          <ul className="gp-plain-list">
            {p.items_moving.map((i) => {
              const c = p.conflicts.find((x) => x.item_id === i.id);
              return <li key={i.id}>{i.title}{c ? <> {' '}<Chip text={`${who} conflicts · goes to the next approver`} tone="amber" /></> : null}</li>;
            })}
          </ul>
        </>
      ) : <p className="gp-dim">{`Nothing is delegated: ${p.items_moving.length + p.items_staying.length} item${p.items_moving.length + p.items_staying.length === 1 ? '' : 's'} wait for you or route to a backup approver.`}</p>}
      {delegate && p.items_staying.length ? (
        <>
          <h3>{`Stays with you · ${p.items_staying.length}`}</h3>
          <ul className="gp-plain-list">{p.items_staying.map((i) => <li key={i.id}>{i.title} <span className="gp-dim">{`· ${i.why}`}</span></li>)}</ul>
        </>
      ) : null}
      {gaps.length ? <Chip text={`Days not covered: ${gaps.map((g) => `${fmtDay(g.from)} – ${fmtDay(g.to)}`).join(', ')}`} tone="amber" /> : null}
    </div>
  );
}

/** Which absence card applies to the viewer right now (proposed by someone else → pending; current → away; ended ≤ 7 days → while you were out). */
export function absenceState(absences: ReadonlyArray<{ absence_id: string; state: string; set_by: string; user_id: string; starts_on: string; ends_on: string; closed_at: string | null }>, viewerId: string, todayDay: string) {
  const mine = absences.filter((a) => a.user_id === viewerId);
  const pending = mine.find((a) => a.state === 'proposed' && a.set_by !== viewerId) ?? null;
  const away = mine.find((a) => a.state === 'confirmed' && a.starts_on <= todayDay && a.ends_on >= todayDay) ?? null;
  const weekAgo = new Date(Date.parse(`${todayDay}T00:00:00Z`) - 7 * 86_400_000).toISOString().slice(0, 10);
  const back = mine.find((a) => (a.state === 'ended' || (a.state === 'confirmed' && a.ends_on < todayDay)) && a.ends_on >= weekAgo) ?? null;
  return { pending, away, back };
}
